import React from 'react';
import './VisualizationCard.css';

interface SecretStatusCount {
  status: string;
  count: number;
}

interface SecretsCardProps {
  secretsByStatus: SecretStatusCount[];
  secretsUsed: number;
  secretsLimit: number;
}

const STATUS_COLORS: Record<string, string> = {
  active: '#10b981',
  viewed: '#f59e0b',
  expired: '#9ca3af',
};

const SecretsCard: React.FC<SecretsCardProps> = ({ secretsByStatus, secretsUsed, secretsLimit }) => {
  const hasSecrets = secretsByStatus.length > 0;
  const maxCount = hasSecrets ? Math.max(...secretsByStatus.map(s => s.count)) : 1;
  const yMid = Math.ceil(maxCount / 2);

  const quotaPercent = secretsLimit > 0 ? Math.min((secretsUsed / secretsLimit) * 100, 100) : 0;
  const atLimit = secretsLimit > 0 && secretsUsed >= secretsLimit;

  return (
    <div className="visualization-card">
      <h3 className="card-title">Secrets</h3>
      {secretsLimit > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: atLimit ? '#dc2626' : '#666', marginBottom: '4px' }}>
            <span>Monthly quota</span>
            <span style={{ fontWeight: 600 }}>{secretsUsed} / {secretsLimit}</span>
          </div>
          <div style={{ width: '100%', height: '6px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${quotaPercent}%`, background: atLimit ? '#dc2626' : 'linear-gradient(90deg, #667eea, #764ba2)', borderRadius: '4px', transition: 'width 0.3s ease' }} />
          </div>
          {atLimit && <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: '3px' }}>Monthly limit reached — resets next month</div>}
        </div>
      )}
      <div className="card-content">
        {!hasSecrets ? (
          <div className="no-data-message">No secrets yet</div>
        ) : (
          <div className="bar-chart" role="img" aria-label="Bar chart showing secrets by status">
            <div style={{ display: 'flex', width: '100%', height: '100%' }}>
              {/* Y axis labels */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                paddingRight: '6px',
                paddingBottom: '36px',
                fontSize: '11px',
                color: '#888',
                alignItems: 'flex-end',
                minWidth: '20px'
              }}>
                <span>{maxCount}</span>
                <span>{yMid}</span>
                <span>0</span>
              </div>
              {/* Bars */}
              <div className="chart-area" style={{ flex: 1 }}>
                {secretsByStatus.map(item => {
                  const heightPercentage = (item.count / maxCount) * 100;
                  const color = STATUS_COLORS[item.status] || '#667eea';
                  const label = item.status.charAt(0).toUpperCase() + item.status.slice(1);
                  return (
                    <div key={item.status} className="bar-item">
                      <div className="bar-wrapper">
                        <div
                          className="bar"
                          style={{ height: `${heightPercentage}%`, backgroundColor: color }}
                          title={`${label}: ${item.count}`}
                        />
                      </div>
                      <div className="bar-label">{label}</div>
                      <div className="bar-value">{item.count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecretsCard;
